import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, ArrowLeft, Check, Circle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fileUrl } from "@/lib/api";
import { fmtDate, fmtNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import AiTextBlock from "@/components/AiTextBlock";

const VIS = [["home_matches", "matchs à domicile avec votre marque"], ["spectators", "spectateurs cumulés au stade"], ["posts", "publications dédiées sur nos réseaux"], ["reach", "personnes touchées en ligne"]];

const DEFAULT_THANKS = "Merci pour votre soutien cette saison. Voici ce que le club a réalisé pour vous jusqu'ici, et la visibilité que vous en avez retirée.";

export default function Bilan() {
  const { id } = useParams();
  const { session } = useAuth();
  const club = session.club;
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/sponsors/${id}`).then((r) => setS(r.data)).catch(() => setS(false)); }, [id]);
  useEffect(() => { if (s) document.title = `Bilan ${s.name} · ${club.name}`; return () => { document.title = "Partenia"; }; }, [s, club.name]);
  const sendMail = async () => {
    if (!window.confirm(`Envoyer ce bilan par email à ${s.contact_name || s.name} (${s.email}) ?`)) return;
    try {
      const { data } = await api.post(`/sponsors/${s.id}/send-bilan`);
      toast.success(data.demo ? "Compte de démo : le bilan a été envoyé à une adresse de test." : `Bilan envoyé à ${s.email}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  if (s === false) return <p>Sponsor introuvable.</p>;
  if (!s) return <p className="text-[#5B665F]">Chargement…</p>;
  const delivered = s.counterparts.filter((c) => c.status === "livre");
  const ordered = [...delivered, ...s.counterparts.filter((c) => c.status !== "livre")];
  const photos = s.counterparts.filter((c) => c.photo_path);
  const today = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="bilan-page">
      <div className="no-print flex flex-wrap items-center justify-between gap-3 mb-6">
        <Link to={`/sponsors/${s.id}`} className="btn-ghost btn-sm" data-testid="bilan-back-link"><ArrowLeft size={15} /> Retour à la fiche</Link>
        <div className="flex flex-wrap gap-2">
          <button className="btn-outline" onClick={sendMail} disabled={!s.email} title={s.email ? "" : "Ajoutez un email au sponsor"} data-testid="bilan-send-email-button"><Send size={16} /> Envoyer par email</button>
          <button className="btn-primary" onClick={() => window.print()} data-testid="bilan-download-pdf-button"><Download size={16} /> Télécharger en PDF</button>
        </div>
      </div>
      <div className="overflow-x-auto -mx-4 sm:mx-0 print:overflow-visible print:m-0">
        <article className="a4 mx-auto bg-white shadow-[0_2px_24px_rgba(19,32,26,0.08)] rounded-[14px] overflow-hidden print:rounded-none max-sm:w-full max-sm:min-h-0" data-testid="bilan-document">
          <header className="bg-[#13201A] text-white px-8 sm:px-12 py-7 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {club.logo_path ? <img src={fileUrl(club.logo_path)} alt="" className="w-14 h-14 rounded-full object-cover bg-white" data-testid="bilan-club-logo" />
                : <span className="w-14 h-14 rounded-full border border-dashed border-white/40 grid place-items-center font-display font-bold text-lg">{club.name.charAt(0)}</span>}
              <div>
                <p className="font-display font-bold text-xl" data-testid="bilan-club-name">{club.name}</p>
                <p className="text-sm text-white/75">Saison {s.season || club.season}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[#3DDC84] text-xs font-bold tracking-[0.12em] uppercase">Bilan de partenariat</p>
              <p className="text-sm text-white/80 mt-1" data-testid="bilan-date">Point au {today}</p>
            </div>
          </header>
          <div className="px-8 sm:px-12 py-9">
            <p className="text-sm text-[#5B665F]">Préparé pour</p>
            <h1 className="text-4xl font-bold tracking-tight mt-1" data-testid="bilan-sponsor-name">{s.name}</h1>
            <div className="max-w-[560px] mt-3" data-testid="bilan-thanks">
              <AiTextBlock s={s} field="thanks_message" placeholder={DEFAULT_THANKS} textClass="text-[15px] text-[#5B665F]" testId="bilan-thanks" />
            </div>

            <h2 className="text-lg font-bold mt-9 mb-3">Vos contreparties · {delivered.length} sur {s.counterparts.length} livrées</h2>
            <ul data-testid="bilan-counterparts">
              {ordered.map((c) => (
                <li key={c.id} className="flex items-center gap-3 py-2.5 border-b border-[#EEF0EB] text-sm avoid-break">
                  {c.status === "livre" ? <Check size={16} className="text-[#1E6B45] shrink-0" /> : <Circle size={14} className="text-[#B86200] shrink-0" />}
                  <span className="flex-1">{c.title}</span>
                  <span className={`text-xs text-right ${c.status === "livre" ? "text-[#5B665F]" : "text-[#8A4B00] font-semibold"}`}>{c.status === "livre" ? `livrée le ${fmtDate(c.delivered_date)}` : `prévue le ${fmtDate(c.due_date)}`}</span>
                </li>
              ))}
            </ul>

            <h2 className="text-lg font-bold mt-9 mb-3">Votre visibilité</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 avoid-break" data-testid="bilan-visibility">
              {VIS.map(([k, label]) => (
                <div key={k} className="bg-[#F6F7F3] rounded-[12px] p-4">
                  <p className="num text-3xl font-bold">{fmtNumber(s.visibility?.[k])}</p>
                  <p className="text-xs text-[#5B665F] mt-1.5 leading-snug">{label}</p>
                </div>
              ))}
            </div>

            <h2 className="text-lg font-bold mt-9 mb-3">En images</h2>
            {photos.length ? (
              <div className="grid grid-cols-2 gap-4" data-testid="bilan-photos">
                {photos.map((c) => (
                  <figure key={c.id} className="avoid-break">
                    <img src={fileUrl(c.photo_path)} alt={c.title} className="w-full aspect-[4/3] object-cover rounded-[12px]" />
                    <figcaption className="text-xs text-[#5B665F] mt-1.5">{c.title}</figcaption>
                  </figure>
                ))}
              </div>
            ) : <p className="text-sm text-[#5B665F] border border-dashed border-[#D5D9D1] rounded-[12px] p-6 text-center" data-testid="bilan-no-photos">Les photos de vos contreparties apparaîtront ici.</p>}

            <section className="mt-8 rounded-[14px] bg-[#E8F3EC] p-5 avoid-break" data-testid="bilan-next-steps">
              <h3 className="text-base font-bold">Et la suite ?</h3>
              <AiTextBlock s={s} field="next_steps" placeholder="Vos prochaines étapes avec le sponsor…" textClass="text-sm mt-2" testId="bilan-next-steps" />
            </section>

            <footer className="mt-10 flex flex-wrap items-end justify-between gap-4 avoid-break" data-testid="bilan-signature">
              <div>
                <p className="font-bold">{club.manager_name || "Responsable partenariats"}</p>
                <p className="text-sm text-[#5B665F]">Responsable partenariats · {club.name}{club.manager_phone && ` · ${club.manager_phone}`}</p>
              </div>
            </footer>
          </div>
        </article>
      </div>
    </div>
  );
}
